import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Plus, Trash2, X } from "lucide-react";
import { api, formatErr, cx } from "../lib/api";
import { useI18n } from "../lib/i18n";

const PRICE_TYPES = ["fixed", "from", "per_hour", "per_day", "per_person", "custom_quote"];
const CURRENCIES = ["EUR", "USD", "GBP", "AED"];
const DURATION_UNITS = ["minutes", "hours", "days", "nights", "weeks", "sessions"];
const MAX_IMAGES = 10;
const DURATION_MODES = ["standard", "custom"];

const linesToArray = (txt) =>
  (txt || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

const blank = {
  destination_id: "",
  category_id: "",
  title: "",
  description: "",
  base_price: "",
  currency: "EUR",
  price_type: "from",
  deposit_percent: 30,
  duration_minutes: "",
  duration_value: "",
  duration_unit: "",
  duration_label: "",
  max_guests: "",
  location: "",
  included: "",
  excluded: "",
  cancellation_policy: "",
  images: "",
  crypto_accepted: true,
};

/**
 * Provider service create / edit modal.
 *
 * Props:
 * - mode: "create" | "edit"
 * - service: existing service object when editing
 * - destinations / categories: arrays for dropdowns
 * - onClose(refresh: boolean): closes modal; refresh=true => parent should reload
 */
export default function ServiceFormModal({ mode, service, destinations = [], categories = [], onClose }) {
  const { t } = useI18n();
  const isEdit = mode === "edit";
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [durationMode, setDurationMode] = useState("standard"); // "standard" | "custom"
  // Service media (edit-mode only) — kept separate from form because images live on the service doc.
  const [mediaImages, setMediaImages] = useState([]);

  useEffect(() => {
    if (isEdit && service) {
      setForm({
        destination_id: service.destination_id || "",
        category_id: service.category_id || "",
        title: service.title || "",
        description: service.description || "",
        base_price: service.base_price ?? "",
        currency: service.currency || "EUR",
        price_type: service.price_type || "from",
        deposit_percent: service.deposit_percent ?? 30,
        duration_minutes: service.duration_minutes ?? "",
        duration_value: service.duration_value ?? "",
        duration_unit: service.duration_unit || "",
        duration_label: service.duration_label || "",
        max_guests: service.max_guests ?? "",
        location: service.location || "",
        included: arrayToLines(service.included),
        excluded: arrayToLines(service.excluded),
        cancellation_policy: service.cancellation_policy || "",
        images: "",
        crypto_accepted: service.crypto_accepted !== false,
      });
      setMediaImages(Array.isArray(service.images) ? service.images : []);
      setDurationMode(service.duration_label ? "custom" : "standard");
    } else {
      setForm({
        ...blank,
        destination_id: destinations[0]?.id || "",
        category_id: categories[0]?.id || "",
      });
      setMediaImages([]);
      setDurationMode("standard");
    }
  }, [mode, service, destinations, categories, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const useCustom = durationMode === "custom";
    const payload = {
      destination_id: form.destination_id,
      category_id: form.category_id,
      title: form.title.trim(),
      description: form.description.trim(),
      base_price: Number(form.base_price),
      currency: form.currency,
      price_type: form.price_type,
      deposit_percent: Number(form.deposit_percent),
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      duration_value: !useCustom && form.duration_value ? Number(form.duration_value) : null,
      duration_unit: !useCustom && form.duration_unit ? form.duration_unit : null,
      duration_label: useCustom ? (form.duration_label.trim() || null) : null,
      max_guests: form.max_guests ? Number(form.max_guests) : null,
      location: form.location.trim() || null,
      included: linesToArray(form.included),
      excluded: linesToArray(form.excluded),
      cancellation_policy: form.cancellation_policy.trim() || null,
      // In edit mode, images are managed via the media endpoints.
      images: isEdit ? mediaImages : linesToArray(form.images),
      crypto_accepted: !!form.crypto_accepted,
    };
    try {
      if (isEdit) {
        await api.patch(`/services/${service.id}`, payload);
      } else {
        await api.post(`/services`, payload);
      }
      onClose(true);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const activeDestinations = destinations.filter((d) => d.status !== "archived" && d.slug !== "custom-trip");

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm" data-testid="service-form-modal">
      <div className="w-full md:max-w-3xl max-h-[95svh] overflow-y-auto modal-panel">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b hairline glass-solid z-10">
          <div>
            <div className="eyebrow">{isEdit ? t("provider_dash.service_form_edit_title", "Edit service") : t("provider_dash.service_form_create_title", "New service")}</div>
            <div className="font-serif-display text-xl mt-1">{form.title || t("provider_dash.field_title_ph", "e.g. Sunset villa with infinity pool")}</div>
          </div>
          <button onClick={() => onClose(false)} className="text-muted hover:text-bone" aria-label="Close" data-testid="service-form-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5" data-testid="service-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("provider_dash.field_destination", "Destination")}>
              <select required value={form.destination_id} onChange={(e) => set("destination_id", e.target.value)} className="input-bare" data-testid="sf-destination">
                <option value="">—</option>
                {activeDestinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} · {d.country}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("provider_dash.field_category", "Category")}>
              <select required value={form.category_id} onChange={(e) => set("category_id", e.target.value)} className="input-bare" data-testid="sf-category">
                <option value="">—</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t("provider_dash.field_title", "Title")}>
            <input required maxLength={120} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("provider_dash.field_title_ph", "e.g. Sunset villa with infinity pool")} className="input-bare" data-testid="sf-title" />
          </Field>

          <Field label={t("provider_dash.field_description", "Description")}>
            <textarea required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("provider_dash.field_description_ph", "Tell guests what makes this experience special.")} className="input-bare resize-y" data-testid="sf-description" />
          </Field>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t("provider_dash.field_base_price", "Base price")}>
              <input required type="number" min="0" step="0.01" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} className="input-bare" data-testid="sf-price" />
            </Field>
            <Field label={t("provider_dash.field_currency", "Currency")}>
              <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="input-bare" data-testid="sf-currency">
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("provider_dash.field_price_type", "Price type")}>
              <select value={form.price_type} onChange={(e) => set("price_type", e.target.value)} className="input-bare" data-testid="sf-price-type">
                {PRICE_TYPES.map((pt) => (
                  <option key={pt} value={pt}>
                    {t(`provider_dash.price_type_${pt}`, pt)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Field label={t("provider_dash.field_deposit_percent", "Deposit %")}>
              <input type="number" min="0" max="100" step="1" value={form.deposit_percent} onChange={(e) => set("deposit_percent", e.target.value)} className="input-bare" data-testid="sf-deposit" />
            </Field>
            <Field label={t("provider_dash.field_duration_minutes", "Duration (minutes)")}>
              <input type="number" min="0" step="1" value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="input-bare" data-testid="sf-duration" />
            </Field>
            <Field label={t("provider_dash.field_max_guests", "Max guests")}>
              <input type="number" min="0" step="1" value={form.max_guests} onChange={(e) => set("max_guests", e.target.value)} className="input-bare" data-testid="sf-max-guests" />
            </Field>
          </div>

          <div className="luxe-card p-4 space-y-4" data-testid="sf-duration-block">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <span className="label-tiny">
                {t("provider_dash.duration_block_title", "Duration (optional)")}
              </span>
              <div className="inline-flex rounded-full p-0.5 bg-ink-deep/40 border hairline" role="tablist" data-testid="sf-duration-mode">
                {DURATION_MODES.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={durationMode === m}
                    onClick={() => setDurationMode(m)}
                    className={cx(
                      "px-3 py-1 text-[11px] rounded-full transition-colors",
                      durationMode === m
                        ? "bg-teal/20 text-teal"
                        : "text-muted hover:text-bone",
                    )}
                    data-testid={`sf-duration-mode-${m}`}
                  >
                    {t(`provider_dash.duration_mode_${m}`, m === "standard" ? "Standard" : "Custom text")}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-muted/70 -mt-2">
              {durationMode === "custom"
                ? t("provider_dash.duration_mode_hint_custom", "Write your own short label (e.g. \"Half-day\", \"Weekend\", \"8h + after-party\").")
                : t("provider_dash.duration_mode_hint_standard", "Pick an amount and a unit (e.g. 4 hours, 3 days).")}
            </p>

            {durationMode === "standard" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("provider_dash.field_duration_value", "Amount")}>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.duration_value}
                    onChange={(e) => set("duration_value", e.target.value)}
                    className="input-bare"
                    placeholder="4"
                    data-testid="sf-duration-value"
                  />
                </Field>
                <Field label={t("provider_dash.field_duration_unit", "Unit")}>
                  <select
                    value={form.duration_unit}
                    onChange={(e) => set("duration_unit", e.target.value)}
                    className="input-bare"
                    data-testid="sf-duration-unit"
                  >
                    <option value="">—</option>
                    {DURATION_UNITS.map((u) => (
                      <option key={u} value={u}>
                        {t(`provider_dash.duration_unit_${u}`, u)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            ) : (
              <Field label={t("provider_dash.field_duration_label", "Custom label")}>
                <input
                  value={form.duration_label}
                  onChange={(e) => set("duration_label", e.target.value)}
                  className="input-bare"
                  placeholder={t("provider_dash.field_duration_label_ph", "Half-day, Weekend, 8h + after-party…")}
                  maxLength={80}
                  data-testid="sf-duration-label"
                />
              </Field>
            )}
          </div>

          <Field label={t("provider_dash.field_location", "Location")}>
            <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder={t("provider_dash.field_location_ph", "Es Vedrà, Ibiza")} className="input-bare" data-testid="sf-location" />
          </Field>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label={t("provider_dash.field_included", "What's included (one per line)")}>
              <textarea rows={4} value={form.included} onChange={(e) => set("included", e.target.value)} className="input-bare resize-y" data-testid="sf-included" />
            </Field>
            <Field label={t("provider_dash.field_excluded", "What's not included (one per line)")}>
              <textarea rows={4} value={form.excluded} onChange={(e) => set("excluded", e.target.value)} className="input-bare resize-y" data-testid="sf-excluded" />
            </Field>
          </div>

          <Field label={t("provider_dash.field_cancellation", "Cancellation policy")}>
            <textarea rows={2} value={form.cancellation_policy} onChange={(e) => set("cancellation_policy", e.target.value)} className="input-bare resize-y" data-testid="sf-cancellation" />
          </Field>

          {isEdit ? (
            <ServiceMediaEditor
              serviceId={service.id}
              images={mediaImages}
              setImages={setMediaImages}
              onError={setError}
            />
          ) : (
            <Field label={t("provider_dash.field_images", "Image URLs (one per line)")}>
              <textarea
                rows={3}
                value={form.images}
                onChange={(e) => set("images", e.target.value)}
                className="input-bare resize-y font-mono text-xs"
                placeholder="https://…"
                data-testid="sf-images"
              />
              <span className="text-[11px] text-muted/70 mt-1.5 block">
                {t("provider_dash.images_create_hint", "Paste URLs here for now — once the service is saved, you can upload your own photos from the edit screen.")}
              </span>
            </Field>
          )}

          <label className="flex items-center gap-3 cursor-pointer text-sm">
            <input type="checkbox" checked={!!form.crypto_accepted} onChange={(e) => set("crypto_accepted", e.target.checked)} className="h-4 w-4 accent-teal" data-testid="sf-crypto" />
            <span>{t("provider_dash.field_crypto_accepted", "Accept crypto payments")}</span>
          </label>

          {error && <div className="text-rose-300 text-sm" data-testid="sf-error">{error}</div>}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
            <button type="button" onClick={() => onClose(false)} className="btn btn-ghost" data-testid="sf-cancel">
              {t("provider_dash.cancel", "Cancel")}
            </button>
            <button type="submit" disabled={busy} className={cx("btn btn-primary", busy && "opacity-60")} data-testid="sf-submit">
              {busy
                ? isEdit
                  ? t("provider_dash.saving_service", "Saving…")
                  : t("provider_dash.creating_service", "Creating…")
                : t("provider_dash.save_service", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tiny block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

/** Inline media manager: upload, reorder, remove. Edit mode only. */
function ServiceMediaEditor({ serviceId, images, setImages, onError }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [reorderBusy, setReorderBusy] = useState(false);
  const remaining = MAX_IMAGES - images.length;

  const onPick = () => inputRef.current?.click();

  const onFiles = async (filesList) => {
    const files = Array.from(filesList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (files.length > remaining) {
      onError?.(t("provider_dash.media_too_many", "You can have up to {{max}} images.", { max: MAX_IMAGES }));
      return;
    }
    setUploading(true);
    onError?.(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f, f.name));
    try {
      const { data } = await api.post(`/services/${serviceId}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages(data?.service?.images || []);
    } catch (e) {
      onError?.(formatErr(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeImage = async (url) => {
    onError?.(null);
    try {
      const { data } = await api.delete(`/services/${serviceId}/media`, { data: { url } });
      setImages(data?.service?.images || []);
    } catch (e) {
      onError?.(formatErr(e));
    }
  };

  const move = async (idx, dir) => {
    const next = [...images];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setImages(next);
    setReorderBusy(true);
    try {
      await api.put(`/services/${serviceId}/media/order`, { images: next });
    } catch (e) {
      onError?.(formatErr(e));
      // Revert on failure
      setImages(images);
    } finally {
      setReorderBusy(false);
    }
  };

  const addUrl = async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (images.length >= MAX_IMAGES) {
      onError?.(t("provider_dash.media_too_many", "You can have up to {{max}} images.", { max: MAX_IMAGES }));
      return;
    }
    onError?.(null);
    try {
      // Use PATCH /services/{id} which accepts the full images list.
      const next = [...images, url];
      const { data } = await api.patch(`/services/${serviceId}`, { images: next });
      setImages(data?.service?.images || next);
      setUrlInput("");
    } catch (e) {
      onError?.(formatErr(e));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    onFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3" data-testid="service-media-editor">
      <div className="flex items-center justify-between">
        <span className="label-tiny">{t("provider_dash.field_images_uploaded", "Photos")}</span>
        <span className="text-[11px] text-muted/70">
          {t("provider_dash.images_count", "{{count}} / {{max}}", { count: images.length, max: MAX_IMAGES })}
        </span>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sf-media-grid">
          {images.map((url, i) => (
            <div key={url} className="relative group rounded-xl overflow-hidden border hairline aspect-[4/3] bg-ink-deep/40">
              <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
              {i === 0 && (
                <span className="absolute top-2 left-2 chip success text-[10px]" data-testid={`sf-media-cover-${i}`}>
                  {t("provider_dash.images_cover", "Cover")}
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 p-2 bg-gradient-to-t from-ink-deep/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0 || reorderBusy}
                    className="p-1.5 rounded-md bg-black/50 hover:bg-black/70 text-bone disabled:opacity-30"
                    aria-label="Move up"
                    data-testid={`sf-media-up-${i}`}
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === images.length - 1 || reorderBusy}
                    className="p-1.5 rounded-md bg-black/50 hover:bg-black/70 text-bone disabled:opacity-30"
                    aria-label="Move down"
                    data-testid={`sf-media-down-${i}`}
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeImage(url)}
                  className="p-1.5 rounded-md bg-rose-500/80 hover:bg-rose-500 text-white"
                  aria-label="Delete"
                  data-testid={`sf-media-del-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cx(
          "rounded-xl border border-dashed hairline p-5 text-center text-sm",
          uploading ? "opacity-60" : "hover:border-teal/60",
        )}
        data-testid="sf-media-dropzone"
      >
        <ImagePlus size={20} className="mx-auto mb-2 text-teal" />
        <div className="text-bone">
          {uploading
            ? t("provider_dash.images_uploading", "Uploading…")
            : t("provider_dash.images_drop_or_browse", "Drag & drop photos, or click to browse")}
        </div>
        <div className="text-[11px] text-muted/70 mt-1">
          {t("provider_dash.images_hint", "JPG / PNG / WebP up to 10MB. We optimise & resize automatically.")}
        </div>
        <button
          type="button"
          onClick={onPick}
          disabled={uploading || remaining <= 0}
          className="btn btn-outline mt-3"
          data-testid="sf-media-pick"
        >
          {t("provider_dash.images_browse", "Choose photos")}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
          data-testid="sf-media-input"
        />
      </div>

      <div className="flex gap-2 items-center">
        <input
          type="url"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder={t("provider_dash.images_url_placeholder", "or paste an image URL")}
          className="input-bare flex-1"
          data-testid="sf-media-url-input"
        />
        <button
          type="button"
          onClick={addUrl}
          disabled={!urlInput.trim() || images.length >= MAX_IMAGES}
          className="btn btn-ghost"
          data-testid="sf-media-url-add"
        >
          <Plus size={14} /> {t("provider_dash.add", "Add")}
        </button>
      </div>
    </div>
  );
}
